export interface LearningConversationLeaseScope {
  userId: string;
  personaId: string;
  modality: 'chat' | 'avatar';
  chapterSlug: string;
}

export interface LearningConversationLease {
  conversationId: string;
  startedAt: number;
}

const LEASE_VERSION = 'v1';
const LEASE_PREFIX = 'learning-conversation';
export const LEARNING_CONVERSATION_LEASE_MS = 3 * 60 * 60 * 1_000;

export function buildLearningConversationLeaseKey(
  scope: LearningConversationLeaseScope,
): string {
  return [
    LEASE_PREFIX,
    LEASE_VERSION,
    scope.userId,
    scope.personaId,
    scope.modality,
    scope.chapterSlug,
  ].map(encodeURIComponent).join(':');
}

export function readLearningConversationLease(
  storage: Storage,
  scope: LearningConversationLeaseScope,
  now: number = Date.now(),
): LearningConversationLease | null {
  const key = buildLearningConversationLeaseKey(scope);
  try {
    const raw = storage.getItem(key);
    if (!raw) return null;

    const parsed = JSON.parse(raw) as Partial<LearningConversationLease>;
    const valid = typeof parsed.conversationId === 'string'
      && parsed.conversationId.trim().length > 0
      && typeof parsed.startedAt === 'number'
      && Number.isFinite(parsed.startedAt)
      && parsed.startedAt >= 0
      && parsed.startedAt <= now
      && now - parsed.startedAt < LEARNING_CONVERSATION_LEASE_MS;
    if (!valid) {
      storage.removeItem(key);
      return null;
    }

    return {
      conversationId: parsed.conversationId!.trim(),
      startedAt: parsed.startedAt!,
    };
  } catch {
    try { storage.removeItem(key); } catch { /* browser storage is optional */ }
    return null;
  }
}

export function writeLearningConversationLease(
  storage: Storage,
  scope: LearningConversationLeaseScope,
  conversationId: string,
  now: number = Date.now(),
): LearningConversationLease {
  const existing = readLearningConversationLease(storage, scope, now);
  const lease = {
    conversationId: conversationId.trim(),
    startedAt: existing?.startedAt ?? now,
  };
  storage.setItem(buildLearningConversationLeaseKey(scope), JSON.stringify(lease));
  return lease;
}

export function advanceLearningConversationLease(
  storage: Storage,
  scope: LearningConversationLeaseScope,
  conversationId: string,
  expectedStartedAt: number,
  now: number = Date.now(),
): LearningConversationLease | null {
  const existing = readLearningConversationLease(storage, scope, now);
  if (!existing || existing.startedAt !== expectedStartedAt) return null;

  const lease = {
    conversationId: conversationId.trim(),
    startedAt: existing.startedAt,
  };
  storage.setItem(buildLearningConversationLeaseKey(scope), JSON.stringify(lease));
  return lease;
}
